import { BagCouponCard } from "@/components/cart/coupon/BagCouponCard"
import { CouponAddRow } from "@/components/cart/coupon/CouponAddRow"
import { CouponCodeForm } from "@/components/cart/coupon/CouponCodeForm"
import { CouponGroupCard } from "@/components/cart/coupon/CouponGroupCard"
import { Button, SectionHeader, Text } from "@/components/ui"
import { VARIANTS } from "@/lib/motion"
import { AnimatePresence, motion } from "motion/react"
import { useNavigate } from "react-router-dom"
import { useCouponWallet } from "@/hooks/use-coupon-wallet"
import { useCheckoutCoupons } from "../../hooks/useCheckoutCoupons"
import { WalletCouponList } from "./WalletCouponList"

type CheckoutCouponSectionProps = {
  /** True while points are used as money: that discount does not add up with a coupon (benefits and rewards do). */
  pointsInUse: boolean
  onUseCouponInstead: () => void
}

/**
 * The checkout's coupon, as the same ticket as the bag: ONE coupon — the one
 * coupon-service applies (or "2 cupons" when they combine), else the one
 * closest to applying — with its state inline, or the "Tenho um cupom" row.
 * Codes that will not ride on the order leave on their own when the verdict
 * comes (`useSettleCouponCodes`).
 */
export function CheckoutCouponSection({ pointsInUse, onUseCouponInstead }: CheckoutCouponSectionProps) {
  const { coupons, focus, remove, submitCode, pick } = useCheckoutCoupons()
  const wallet = useCouponWallet()
  const navigate = useNavigate()

  return (
    <section aria-label="Cupom">
      <SectionHeader title="Cupom" />
      <AnimatePresence mode="wait" initial={false}>
        {pointsInUse ? (
          <motion.div key="points" variants={VARIANTS.fadeUp} initial="hidden" animate="visible" exit="exit" className="flex flex-wrap items-baseline gap-x-2">
            <Text size="sm" tone="muted">
              Você escolheu usar pontos como desconto neste pedido.
            </Text>
            <Button variant="link" tone="primary" size="auto" weight="semibold" onClick={onUseCouponInstead}>
              <Text as="span" size="sm">
                Usar cupom
              </Text>
            </Button>
          </motion.div>
        ) : focus ? (
          <motion.ul key="coupon" variants={VARIANTS.fadeUp} initial="hidden" animate="visible" exit="exit">
            {focus.kind === "group" ? (
              <CouponGroupCard coupons={focus.coupons} discountCents={focus.discountCents} onRemove={remove} />
            ) : (
              <BagCouponCard
                coupon={focus.coupon}
                onRemove={() => remove(focus.coupon.code)}
                onAddItems={() => navigate("/")}
                replaceForm={(close) => (
                  <CouponCodeForm replacing={focus.coupon.code} onSubmitCode={(raw) => submitCode(raw, focus.coupon.code)} onDone={close} onCancel={close} />
                )}
              />
            )}
          </motion.ul>
        ) : (
          <motion.div key="add" variants={VARIANTS.fadeUp} initial="hidden" animate="visible" exit="exit">
            <CouponAddRow form={(close) => <CouponCodeForm onSubmitCode={(raw) => submitCode(raw)} onDone={close} onCancel={close} />} />
          </motion.div>
        )}
      </AnimatePresence>
      {pointsInUse ? null : <WalletCouponList wallet={wallet} applied={coupons.map((coupon) => coupon.code)} onPick={pick} />}
    </section>
  )
}
